/**
 * Shared API contract types — mirror cx-commons `ApiResponse<T>`
 * (see cx-commons/.../response/ApiResponse.java).
 *
 * Field names are camelCase: the server uses Jackson defaults, no naming
 * strategy override is configured in application.yml.
 *
 * Success envelope:
 *   { success:true, message, data, meta }
 *
 * Error envelope (HTTP non-2xx):
 *   { success:false, error: { code, message, field?, traceId, timestamp } }
 *
 * `data`, `meta`, `error`, `message` are all `@JsonInclude(NON_NULL)` on the
 * server, so they're optional in TypeScript too.
 */

export interface ResponseMeta {
  tenantId: string;
  calculationDate?: string;
  engineVersion?: string;
  traceId: string;
  timestamp: string;
  durationMs?: number;
}

export interface ResponseError {
  code: string;
  message: string;
  field?: string;
  traceId: string;
  timestamp: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  meta?: ResponseMeta;
  error?: ResponseError;
}

/**
 * Page envelope used by paginated endpoints
 * (see Architecture v4 §3.5 — pagination block lives at top level).
 */
export interface PaginationMeta {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PagedResponse<T> extends ApiResponse<T[]> {
  pagination: PaginationMeta;
}

/** JWT payload — see Technical Architecture §2.1. */
export interface JwtPayload {
  sub: string;
  email: string;
  tenant_id: string;
  roles: string[];
  permissions: string[];
  exp: number;
  iss: string;
}

/**
 * Thrown by `apiGet/apiPost/...` so callers can show a useful message and
 * act on the server-supplied error code (see GlobalExceptionHandler.java).
 */
export class CubixApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly traceId?: string;
  readonly field?: string;

  constructor(
    message: string,
    code: string,
    status: number,
    options: { traceId?: string; field?: string } = {},
  ) {
    super(message);
    this.name = 'CubixApiError';
    this.code = code;
    this.status = status;
    this.traceId = options.traceId;
    this.field = options.field;
  }
}
